import { ReactElement } from 'react';

import { ResponseStatus } from '@revenuemarket/http';
import {
  createPartnerInfoFromApi,
  getPartnerByHostname,
  PartnerInfo,
} from '@revenuemarket/utils/partners/getPartnerByHostname';
import { GetServerSidePropsContext } from 'next';
import { getPartners, BasicPartnerListResponse } from '~apis/partners';
import LegacyPartnerLanding from '~components/PartnerLanding/LegacyPartnerLanding';
import PartnerLanding from '~components/PartnerLanding/PartnerLanding';

const APP_URL = 'app.clobe.ai';

interface IntroPageProps {
  host: string;
  partnersData: BasicPartnerListResponse | null;
  isDownloadMode: boolean;
}

const IntroPage = ({ host, partnersData, isDownloadMode }: IntroPageProps) => {
  try {
    let partnerMap: PartnerInfo[] | undefined = undefined;

    // API 데이터가 있으면 사용, 없으면 fallback static 데이터 사용
    if (partnersData && partnersData.partners && partnersData.partners.length > 0) {
      partnerMap = createPartnerInfoFromApi(partnersData.partners);
    } else {
      // getPartnerByHostname의 두 번째 파라미터를 undefined로 전달하면 자동으로 FALLBACK_PARTNER_MAP 사용
      partnerMap = undefined;
    }

    const partner = partnerMap
      ? getPartnerByHostname(host, partnerMap)
      : getPartnerByHostname(host);

    if (!partner) {
      console.warn(`파트너를 찾을 수 없습니다. Host: ${host}`);
      return <div>고객 전용 서비스입니다.</div>;
    }

    if (isDownloadMode) {
      return <LegacyPartnerLanding title={partner.title} value={partner.value} />;
    }
    return <PartnerLanding title={partner.title} value={partner.value} />;
  } catch (error) {
    console.error('파트너 정보 처리 중 오류가 발생했습니다:', error);
    return <div>고객 전용 서비스입니다.</div>;
  }
};

// 레이아웃을 완전히 제거하여 독립적인 페이지로 만듦
IntroPage.getLayout = function getLayout(page: ReactElement) {
  return page;
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const host = context.req.headers.host;
  const isDownloadMode = context.query?.download === '1';

  if (host?.includes(APP_URL)) {
    return {
      redirect: {
        permanent: true,
        destination: 'https://app.clobe.ai',
      },
    };
  }

  try {
    // 서버 사이드에서 getPartners API 호출
    const response = await getPartners();

    let partnersData: BasicPartnerListResponse | null = null;

    if (response.Status === ResponseStatus.SUCCESS && response.Data) {
      partnersData = response.Data.data;
    } else {
      console.warn('⚠️ [SSR] Partners API returned unsuccessful response:', {
        status: response.Status,
        statusCode: response.StatusCode,
        errorCode: response.ErrorCode,
        errorMsg: response.ErrorMsg,
        hasData: !!response.Data,
        rawResponse: response,
      });
    }

    return {
      props: {
        host,
        partnersData,
        isDownloadMode,
      },
    };
  } catch (error) {
    console.error('❌ [SSR] Failed to fetch businessPartners on server side:', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : 'Unknown',
    });

    return {
      props: {
        host,
        partnersData: null,
        isDownloadMode,
      },
    };
  }
};

export default IntroPage;
